import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import { injectIntl } from '../../../../util/reactIntl';
import { ListingCard, NamedLink } from '../../../../components';
import { recentListings } from '../../../../util/api';

import Field, { hasDataInFields } from '../../Field';
import SectionContainer from '../SectionContainer';
import css from './SectionRecentListings.module.css';

const KEY_CODE_ARROW_LEFT = 37;
const KEY_CODE_ARROW_RIGHT = 39;


const SectionRecentListings = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    numColumns,
    title,
    description,
    appearance,
    callToAction,
    options,
    intl,
  } = props;

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const sliderContainerId = `${sectionId}-container`;
  const sliderId = `${sectionId}-slider`;

  useEffect(() => {
    const fetchRecentListings = async () => {
      try {
        setLoading(true);
        const response = await recentListings({ count: numColumns * 3 });

        // The response is properly deserialized by api.js
        const responseData = response.data;
        const listingsData = responseData.data || [];
        const included = responseData.included || [];

        // Filter listings that are actual listing objects (not references)
        const actualListings = included.filter(item => item.type === 'listing');

        // Map listing objects with their related data
        const fullListings = actualListings.map(listing => {
          // Add author from included array
          const authorId = listing.relationships?.author?.data?.id?.uuid;
          const author = included.find(item => item.id?.uuid === authorId && item.type === 'user');

          // Add images from included array
          const imageRelationships = listing.relationships?.images?.data || [];
          const images = imageRelationships
            .map(rel => included.find(item => item.id?.uuid === rel.id?.uuid && item.type === 'image'))
            .filter(Boolean);

          // Return listing with images and author properly attached
          return {
            ...listing,
            author,
            images
          };
        });

        setListings(fullListings);
      } catch (err) {
        console.error('Error fetching recent listings:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentListings();
  }, [numColumns]);

  useEffect(() => {
    const setCarouselWidth = () => {
      if (listings.length > 0) {
        const windowWidth = window.innerWidth;
        const elem = window.document.getElementById(sliderContainerId);
        if (elem) {
          const scrollbarWidth = window.innerWidth - document.body.clientWidth;
          const elementWidth =
            elem.clientWidth >= windowWidth - scrollbarWidth ? windowWidth : elem.clientWidth;
          const carouselWidth = elementWidth - scrollbarWidth;

          elem.style.setProperty('--carouselWidth', `${carouselWidth}px`);
        }
      }
    };
    setCarouselWidth();

    window.addEventListener('resize', setCarouselWidth);
    return () => window.removeEventListener('resize', setCarouselWidth);
  }, [listings.length]);

  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);

  const onSlideLeft = e => {
    const slider = window.document.getElementById(sliderId);
    slider.scrollLeft = slider.scrollLeft - slider.clientWidth;
    e.target.focus();
  };

  const onSlideRight = e => {
    const slider = window.document.getElementById(sliderId);
    slider.scrollLeft = slider.scrollLeft + slider.clientWidth;
    e.target.focus();
  };

  const onKeyDown = e => {
    if (e.keyCode === KEY_CODE_ARROW_LEFT) {
      e.preventDefault();
      onSlideLeft(e);
    } else if (e.keyCode === KEY_CODE_ARROW_RIGHT) {
      e.preventDefault();
      onSlideRight(e);
    }
  };

  if (loading) {
    return (
      <SectionContainer
        id={sectionId}
        className={className}
        rootClassName={rootClassName}
        appearance={appearance}
        options={fieldOptions}
      >
        <div className={css.loading}>Loading recent listings...</div>
      </SectionContainer>
    );
  }

  if (error) {
    return (
      <SectionContainer
        id={sectionId}
        className={className}
        rootClassName={rootClassName}
        appearance={appearance}
        options={fieldOptions}
      >
        <div className={css.error}>Unable to load recent listings.</div>
      </SectionContainer>
    );
  }

  const hasListings = listings.length > 0;
  const numberOfListings = listings.length;

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      <div>
        {hasHeaderFields ? (
          <header className={classNames(defaultClasses.sectionDetails, css.headerContainer)}>
            <Field data={title} className={defaultClasses.title} options={fieldOptions} />
            <NamedLink
              name="SearchPage"
              className={defaultClasses.ctaButton}
            >
              {intl.formatMessage({ id: 'SectionRecentListings.exploreAllListings', defaultMessage: 'Explore all listings' })}
            </NamedLink>
            <Field data={description} className={defaultClasses.description} options={fieldOptions} />
            <Field data={callToAction} className={defaultClasses.ctaButton} options={fieldOptions} />
          </header>
        ) : null}
        <div className={css.sectionContainer}>
          {hasListings ? (
            <div className={css.carouselContainer} id={sliderContainerId}>
            <div
              className={classNames(css.carouselArrows, {
                [css.notEnoughListings]: numberOfListings <= numColumns,
              })}
            >
              <button className={css.carouselArrowPrev} onClick={onSlideLeft} onKeyDown={onKeyDown}>
                ‹
              </button>
              <button className={css.carouselArrowNext} onClick={onSlideRight} onKeyDown={onKeyDown}>
                ›
              </button>
            </div>
            <div className={classNames(css.listingsGrid, css[`columns${numColumns}`])} id={sliderId}>
              {listings.map((listing, index) => (
                <ListingCard
                  key={listing.id.uuid}
                  listing={listing}
                  intl={intl}
                  className={css.listingCardItem}
                />
              ))}
            </div>
          </div>
          ) : (
            <div className={css.noListings}>No recent listings found.</div>
          )}
        </div>
      </div>
    </SectionContainer>
  );
};

export default injectIntl(SectionRecentListings);